// DEBT-VITA™ — Orchestrateur PDF (capture les graphes live + monte template offscreen)
import React from "react";
import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas";
import { generatePdf } from "@/lib/report-generators/pdf";
import DebtReportTemplate from "@/components/debt/DebtReportTemplate";

async function captureNode(node, scale = 2) {
  if (!node) return null;
  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* ignore */
    }
  }
  const canvas = await html2canvas(node, {
    scale,
    backgroundColor: "#FFFFFF",
    useCORS: true,
    allowTaint: false,
    logging: false,
    imageTimeout: 15000,
  });
  return canvas.toDataURL("image/jpeg", 0.92);
}

async function captureLiveCharts() {
  // Captures the 8 chart cards present on the dashboard.
  // We cap at 9 just in case.
  const titles = {
    "debt-graph-1": "Thermomètre vitaliste",
    "debt-graph-2": "Anatomie de la dette par créancier",
    "debt-graph-3": "Calendrier de vie : Dette / Fiscalité / Soi-même",
    "debt-graph-4": "Projection 3 scénarios",
    "debt-graph-5": "Charge intergénérationnelle",
    "debt-graph-6": "Dette vs Fiscalité",
    "debt-graph-7": "Service de la dette : intérêts vs capital",
    "debt-graph-8": "Répartition vitaliste (treemap)",
  };
  const out = [];
  for (let i = 1; i <= 8; i += 1) {
    const node = document.querySelector(`[data-testid="debt-graph-${i}"]`);
    if (!node) continue;
    // eslint-disable-next-line no-await-in-loop
    const dataUrl = await captureNode(node, 2);
    out.push({ title: titles[`debt-graph-${i}`] || `Graphique ${i}`, dataUrl });
  }
  return out;
}

/**
 * Monte le template DEBT-VITA™ off-screen, attend le rendu, et appelle
 * generatePdf() qui parcourt les <section data-report-section>.
 */
export async function generateDebtPdf({ project, params, results, narrative }) {
  // 1) Capture live charts (must be done while user is on dashboard)
  const chartImages = await captureLiveCharts();
  const projectionImage = chartImages.find((c) => c.title.startsWith("Projection"))?.dataUrl;

  // 2) Mount offscreen React template
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-10000px";
  host.style.top = "0";
  host.style.zIndex = "-1";
  document.body.appendChild(host);
  const root = createRoot(host);
  root.render(
    <DebtReportTemplate
      project={project}
      params={params}
      results={results}
      narrative={narrative}
      chartImages={chartImages}
      projectionImage={projectionImage}
      generatedAt={new Date().toISOString()}
    />
  );

  // 3) Wait for the DOM to actually render the template
  await new Promise((r) => setTimeout(r, 350));

  try {
    const today = new Date().toISOString().slice(0, 10);
    const countryRaw = project?.country || params?.country || "";
    const country = (countryRaw && countryRaw !== "—" ? countryRaw : "Pays_non_specifie").replace(/\s+/g, "_");
    const year = params?.fiscal_year || project?.fiscal_year || "----";
    const filename = `VITAE-PUBLICA_DEBT-VITA_${country}_${year}_${today}.pdf`;
    const rootNode = host.querySelector("#debt-report-template-root") || host.firstChild;
    const size = await generatePdf({ rootNode, filename });
    return { filename, size };
  } finally {
    // Cleanup
    try {
      root.unmount();
    } catch {
      /* ignore */
    }
    if (host.parentNode) host.parentNode.removeChild(host);
  }
}
